import { MapPin, X } from 'lucide-react'
import { useMemo } from 'react'
import { useAppStore } from '@core/store'
import { isFeatureEnabled } from '@core/features'
import { contarGrupos, filterPins, resumoDosFiltros } from './lib/pinFilter'
import { usePinFiltro } from './usePinFiltro'

/** Botão pequeno no canto do 3D, só quando há pins: "Pins (1191)".
 *
 *  O painel dos pins existia (régua e friso), mas quem via os pins sobre
 *  o modelo não encontrava por onde os filtrar. Este botão fica ao pé
 *  dos próprios pins e abre o painel. Com filtros a esconder alguns,
 *  diz quantos se mostram de quantos há e, ao lado, como se está a
 *  filtrar ("Prioridade: Critical · Especialidade: EST"), com o "Limpar"
 *  à mão (pedido do Rui, 15/10/2026: filtrava e não sabia por quê). */
export default function BcfPinsBotao() {
  const topics = useAppStore((s) => s.bcfPinTopics)
  const visible = useAppStore((s) => s.bcfPinsVisible)
  const open = useAppStore((s) => s.bcfPinsPanelOpen)
  const setOpen = useAppStore((s) => s.setBcfPinsPanelOpen)
  const filtro = usePinFiltro()
  const aMostrar = useMemo(() => (visible ? filterPins(topics, filtro).length : 0), [visible, topics, filtro])
  const criterios = useAppStore((s) => s.bcfPinsCriterios)
  const hideClosed = useAppStore((s) => s.bcfPinsHideClosed)
  const seguirModulo = useAppStore((s) => s.bcfPinsSeguirModulo)
  const vista = useAppStore((s) => s.bcfVistaDaLista)
  const limpar = useAppStore((s) => s.limparFiltrosDosPins)
  const setSeguirModulo = useAppStore((s) => s.setBcfPinsSeguirModulo)
  const nomesDosGrupos = useMemo(() => new Map(contarGrupos(topics).map((g) => [g.chave, g.nome])), [topics])
  const comoFiltra = useMemo(() => {
    const linhas = resumoDosFiltros(criterios, hideClosed, (k) => nomesDosGrupos.get(k) ?? k)
    if (seguirModulo && vista) {
      const r = vista.resumo ?? []
      linhas.push(r.length > 0 ? `lista do BCF (${r.join(', ')})` : 'lista do BCF (sem filtros)')
    }
    return linhas
  }, [criterios, hideClosed, seguirModulo, vista, nomesDosGrupos])

  if (!isFeatureEnabled('bcfPins') || topics.length === 0) return null

  const total = topics.length
  const texto = aMostrar === total ? `Pins (${total})` : `Pins (${aMostrar} de ${total})`
  return (
    <>
    <button
      type="button"
      aria-pressed={open}
      data-campo="botao-pins"
      onClick={() => setOpen(!open)}
      title={`${aMostrar} de ${total} pins BCF à vista. ${open ? 'Fecha' : 'Abre'} o painel dos pins (filtros e desenho).`}
      className={`flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border px-2 py-1 text-[11px] shadow-md backdrop-blur ${
        open
          ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
          : 'border-[var(--color-border)] bg-[var(--color-surface)]/90 text-[var(--color-text)] hover:text-[var(--color-accent)]'
      }`}
    >
      <MapPin size={12} aria-hidden="true" />
      {texto}
    </button>
    {visible && comoFiltra.length > 0 && (
      <div
        data-campo="pins-como-filtra"
        className="flex min-w-0 max-w-[min(32rem,45vw)] items-center gap-1 whitespace-nowrap rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]/90 px-2 py-1 text-[11px] text-[var(--color-text-muted)] shadow-md backdrop-blur"
        title={`A filtrar os pins por: ${comoFiltra.join('; ')}`}
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex min-w-0 items-center gap-1 rounded text-left hover:text-[var(--color-accent)]"
          title={`A filtrar os pins por: ${comoFiltra.join('; ')}. Clique para mudar os filtros.`}
        >
          <span className="shrink-0">A filtrar:</span>
          <span className="min-w-0 truncate text-[var(--color-text)] underline decoration-dotted underline-offset-2">
            {comoFiltra.join(' · ')}
          </span>
        </button>
        <button
          type="button"
          onClick={() => {
            limpar()
            setSeguirModulo(false)
          }}
          aria-label="Limpar os filtros dos pins"
          title="Limpar os filtros dos pins (volta a mostrar todos)"
          className="ml-1 flex shrink-0 items-center gap-0.5 rounded px-1 text-[var(--color-text-dim)] hover:text-[var(--color-accent)]"
        >
          <X size={11} aria-hidden="true" />
          Limpar
        </button>
      </div>
    )}
    </>
  )
}
